/**
 * TrackMap — the hero. Draws the layout (main path, derived sector lines,
 * corners, measured pit lane) with a fixed per-layout transform and places
 * each car from its own motion samples, interpolated along the path per frame.
 *
 * Cars with no location evidence are not drawn on the circuit; they are
 * listed in the "no position" strip instead of being spread by standings.
 */
import React, { useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { useRaceFrame, useRaceSession } from "../RaceSessionProvider";
import { positionQuality, renderEntryMotion } from "../playback";
import { zoomProjector } from "../geometry";
import { useInspector } from "../inspector/InspectorContext";

// Developer-only inspector UI: a separate chunk, fetched only when ?inspect=1 enables it.
const InspectorLayers = React.lazy(() => import("../inspector/InspectorLayers"));

const LABEL_SLOTS = [
    { dx: 0, dy: -30 },
    { dx: 46, dy: -16 },
    { dx: -46, dy: -16 },
    { dx: 0, dy: 28 },
    { dx: 56, dy: 14 },
    { dx: -56, dy: 14 },
    { dx: 0, dy: -52 },
    { dx: 72, dy: -30 },
    { dx: -72, dy: -30 },
];
const LABEL_W = 62;
const LABEL_H = 22;

export const LABEL_MODES = ["minimal", "battles", "all"];
/** A position older than this is no evidence of where the car is now: the car leaves the map for the no-position strip. */
export const HIDE_STALE_AFTER_SECONDS = 30;
const OUT_STATUSES = ["retired", "dsq", "dns"];

function overlaps(a, b) {
    return Math.abs(a.x - b.x) < (a.w + b.w) / 2 && Math.abs(a.y - b.y) < (a.h + b.h) / 2;
}

export function placeLabels(cars, viewBox) {
    const placed = [];
    const result = new Map();
    cars.forEach((car) => {
        let chosen = null;
        for (const slot of LABEL_SLOTS) {
            const candidate = { x: car.x + slot.dx, y: car.y + slot.dy, w: LABEL_W, h: LABEL_H };
            if (candidate.x - LABEL_W / 2 < 4 || candidate.x + LABEL_W / 2 > viewBox.width - 4 || candidate.y - LABEL_H / 2 < 4 || candidate.y + LABEL_H / 2 > viewBox.height - 4) continue;
            if (!placed.some((other) => overlaps(candidate, other))) {
                chosen = candidate;
                break;
            }
        }
        if (!chosen) chosen = { x: car.x, y: car.y - 30, w: LABEL_W, h: LABEL_H };
        placed.push(chosen);
        result.set(car.entryId, chosen);
    });
    return result;
}

/** Screen point for a rendered motion state: the pit-lane fraction, a blended raw point, or progress on the path. */
export function placeCar(view, motion) {
    if (motion.status === "unavailable") return null;
    if (motion.branch === "pit_lane" && view.hasPit && motion.pit != null) return view.pitPointAt(motion.pit);
    if (motion.blend && motion.x != null && motion.y != null) return { ...view.toScreen(motion.x, motion.y), heading: view.pointAt(motion.progress).heading };
    if (motion.progress != null && motion.branch !== "off_track") return view.pointAt(motion.progress);
    if (motion.x != null && motion.y != null) return { ...view.toScreen(motion.x, motion.y), heading: motion.progress != null ? view.pointAt(motion.progress).heading : 0 };
    if (motion.progress != null) return view.pointAt(motion.progress);
    return null;
}

function CarGlyph({ x, y, heading, colour, active, dim, number }) {
    const transform = `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${(heading || 0).toFixed(1)})`;
    const size = active ? 1.35 : 1;
    return (
        <g transform={transform} opacity={dim ? 0.45 : 1}>
            <g transform={`scale(${size})`}>
                <path d="M -9 -3.6 L 4 -3.6 Q 9 -3.6 9 0 Q 9 3.6 4 3.6 L -9 3.6 Z" fill={colour} stroke={active ? "#FFFFFF" : "rgba(4,4,5,0.9)"} strokeWidth={active ? 1.6 : 1.1} />
                <rect x="-8" y="-5" width="4" height="10" rx="1" fill="rgba(0,0,0,0.55)" />
                <rect x="3.5" y="-4.6" width="3" height="9.2" rx="1" fill="rgba(0,0,0,0.45)" />
            </g>
            <text x="0" y="12" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="7" fontWeight="700" fill="rgba(255,255,255,0.78)" transform={`rotate(${-(heading || 0)})`}>
                {number}
            </text>
        </g>
    );
}

export default function TrackMap({ className = "" }) {
    const session = useRaceSession();
    const { snapshot, projector, layout, selectedEntryId, compareEntryId, follow, selectEntry, layoutLengthM, mode, events, cursor } = session;
    const { frameTick, cursorRef, motionStore } = useRaceFrame(); // per-frame: the animation cursor and motion arrivals
    const prefersReducedMotion = useReducedMotion();
    const inspector = useInspector();
    const [labelMode, setLabelMode] = useState("battles");
    const [hoveredId, setHoveredId] = useState(null);
    const lastRendered = useRef(new Map());

    const entryList = snapshot?.entries;
    const entries = useMemo(() => entryList || [], [entryList]);
    const t = cursorRef.current.cursor;
    const viewBox = projector?.viewBox;

    // Developer inspector zoom: one extra uniform transform of the same projector (glyphs keep their size).
    const zoomSpec = inspector?.zoom;
    const view = useMemo(() => {
        if (!projector || !(zoomSpec?.k > 1)) return projector;
        let center = zoomSpec.cx != null ? { x: zoomSpec.cx, y: zoomSpec.cy } : { x: projector.viewBox.width / 2, y: projector.viewBox.height / 2 };
        if (zoomSpec.preset === "selected" && selectedEntryId) {
            const point = placeCar(projector, renderEntryMotion(motionStore, selectedEntryId, t, layoutLengthM));
            if (point) center = point;
        }
        return zoomProjector(projector, { cx: center.x, cy: center.y, k: zoomSpec.k });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projector, zoomSpec, selectedEntryId, motionStore, t, layoutLengthM, frameTick]);

    const cars = useMemo(() => {
        if (!view) return [];
        const rendered = [];
        entries.forEach((row) => {
            const motion = renderEntryMotion(motionStore, row.entry_id, t, layoutLengthM);
            const point = motion.status === "stale" && motion.sampleAge > HIDE_STALE_AFTER_SECONDS ? null : placeCar(view, motion);
            if (!point) {
                lastRendered.current.delete(row.entry_id);
                return;
            }
            rendered.push({ entryId: row.entry_id, row, motion, quality: positionQuality(motion), x: point.x, y: point.y, heading: point.heading });
        });
        return rendered;
        // frameTick changes whenever new motion samples arrive or the clock advances.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, view, motionStore, t, layoutLengthM, frameTick]);

    const battleIds = useMemo(() => {
        const ids = new Set();
        entries.forEach((row) => {
            if (row.interval_kind === "seconds" && row.interval_seconds != null && row.interval_seconds <= 1.0 && row.position > 1) {
                ids.add(row.entry_id);
                const ahead = entries.find((other) => other.position === row.position - 1);
                if (ahead) ids.add(ahead.entry_id);
            }
        });
        return ids;
    }, [entries]);

    const recentEventIds = useMemo(() => {
        const ids = new Set();
        (events || []).forEach((event) => {
            if (event.status !== "retracted" && event.session_seconds != null && cursor - event.session_seconds >= 0 && cursor - event.session_seconds <= 90) (event.participants || []).forEach((id) => ids.add(id));
        });
        return ids;
    }, [events, cursor]);

    const labelled = cars.filter((car) => {
        if (labelMode === "all") return true;
        if (car.entryId === selectedEntryId || car.entryId === compareEntryId || car.entryId === hoveredId || car.row.position === 1) return true;
        if (recentEventIds.has(car.entryId)) return true;
        if (labelMode === "battles" && battleIds.has(car.entryId)) return true;
        return false;
    });
    const labels = useMemo(() => (viewBox ? placeLabels(labelled, viewBox) : new Map()), [labelled, viewBox]);
    const withoutPosition = entries.filter((row) => !cars.some((car) => car.entryId === row.entry_id) && row.status !== "dns");
    // Finishing is per car: the leader's flag does not end anyone else's race.
    const finishedCount = entries.filter((row) => row.status === "finished").length;
    // Still finishing: running cars and cars in a normal pit passage; not cars parked in the pit lane for minutes
    // (retired before the classification says so) and not cars that stopped on track.
    const stillRacing = entries.filter((row) => row.status === "running" || (row.status === "in_pit" && !(row.status_note || "").includes("for"))).length;
    const finishState = finishedCount === 0 ? null : stillRacing > 0 ? "leader_finished" : "all_finished";

    const followTransform = useMemo(() => {
        if (!follow || !selectedEntryId || !viewBox) return null;
        const car = cars.find((item) => item.entryId === selectedEntryId);
        if (!car) return null;
        const zoom = 2.2;
        const tx = viewBox.width / 2 - car.x * zoom;
        const ty = viewBox.height / 2 - car.y * zoom;
        return `translate(${tx.toFixed(1)} ${ty.toFixed(1)}) scale(${zoom})`;
    }, [follow, selectedEntryId, cars, viewBox]);

    if (!projector || !layout) {
        return (
            <div className={`relative overflow-hidden rounded-[28px] border border-white/8 bg-[#050506] ${className}`} data-testid="race-track-map">
                <div className="grid h-full min-h-[320px] place-items-center text-sm text-zinc-500">
                    <div className="rounded-2xl border border-white/8 bg-black/70 px-5 py-4 text-center backdrop-blur-xl">
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">Track status</div>
                        <div className="mt-2">Circuit layout unavailable for this session.</div>
                    </div>
                </div>
            </div>
        );
    }

    const coverage = layout.coverage || {};
    const idPrefix = layout.layout_id.replace(/[^a-z0-9]/gi, "");

    return (
        <div className={`relative overflow-hidden rounded-[28px] border border-white/8 bg-[#050506] shadow-[0_32px_80px_rgba(0,0,0,0.42)] ${className}`} data-testid="race-track-map" data-frame-t={t.toFixed(3)} data-state-t={snapshot?.clock?.session_seconds != null ? Number(snapshot.clock.session_seconds).toFixed(3) : undefined} onClick={() => selectEntry(null)}>
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(225,6,0,0.08),transparent_42%),radial-gradient(circle_at_80%_12%,rgba(255,255,255,0.04),transparent_24%)]" />
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-25" />
            <svg viewBox={`0 0 ${viewBox.width} ${viewBox.height}`} className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${layout.name} track map`}>
                <defs>
                    <linearGradient id={`surface-${idPrefix}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.92)" />
                        <stop offset="35%" stopColor="rgba(244,244,245,0.92)" />
                        <stop offset="70%" stopColor="rgba(225,6,0,0.9)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0.84)" />
                    </linearGradient>
                    <filter id={`blur-${idPrefix}`} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="8" />
                    </filter>
                    <filter id={`glow-${idPrefix}`} x="-100%" y="-100%" width="300%" height="300%">
                        <feGaussianBlur stdDeviation="5" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                <g transform={followTransform || undefined} style={{ transition: prefersReducedMotion ? "none" : "transform 420ms cubic-bezier(0.22,1,0.36,1)" }}>
                    <path d={view.pathD} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="30" strokeLinecap="round" strokeLinejoin="round" filter={`url(#blur-${idPrefix})`} />
                    <path d={view.pathD} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
                    <path d={view.pathD} fill="none" stroke="rgba(4,4,5,0.96)" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
                    <path d={view.pathD} fill="none" stroke={`url(#surface-${idPrefix})`} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" data-testid="race-track-path" />
                    {view.pitD ? <path d={view.pitD} fill="none" stroke="rgba(240,180,41,0.65)" strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round" data-testid="race-track-pit-lane" /> : null}
                    {view.sectorLines.map((line) => (
                        <g key={`sector-${line.sector}`} data-testid="race-track-sector-line">
                            <line x1={line.x} y1={line.y} x2={line.x} y2={line.y} stroke="rgba(225,6,0,0.9)" strokeWidth="12" strokeLinecap="round" transform={`rotate(${line.heading + 90} ${line.x} ${line.y})`} />
                            <text x={line.x + 9} y={line.y - 8} fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(225,6,0,0.9)">
                                S{line.sector === 3 ? "F" : line.sector}
                            </text>
                        </g>
                    ))}
                    {view.corners.map((corner) => (
                        <text key={`corner-${corner.number}`} x={corner.screen.x} y={corner.screen.y} fontFamily="JetBrains Mono, monospace" fontSize="8" fill="rgba(161,161,170,0.75)" textAnchor="middle">
                            T{corner.number}
                        </text>
                    ))}
                    <g opacity="0.9">
                        <line x1={view.startFinish.x} y1={view.startFinish.y} x2={view.startFinish.x} y2={view.startFinish.y} stroke="#fff" strokeWidth="14" strokeLinecap="butt" transform={`rotate(${view.startFinish.heading + 90} ${view.startFinish.x} ${view.startFinish.y})`} strokeDasharray="2 2" />
                    </g>
                    {inspector ? (
                        <React.Suspense fallback={null}>
                            <InspectorLayers view={view} layout={layout} t={t} cars={cars} rows={entries} selectedIds={[selectedEntryId, compareEntryId]} motionStore={motionStore} lengthM={layoutLengthM} />
                        </React.Suspense>
                    ) : null}
                    {cars.map((car) => {
                        const active = car.entryId === selectedEntryId || car.entryId === compareEntryId || car.entryId === hoveredId;
                        // Rings say how far to trust the drawn point (see playback.positionQuality).
                        const unreliable = car.quality === "projected" || car.motion.status === "stale" || (car.quality === "held" && (car.motion.gapSeconds ?? 0) > 0);
                        const estimate = car.motion.status === "bridged" || (car.motion.status === "extrapolated" && car.motion.sampleAge > 1);
                        const reacquired = !!car.motion.reacquired;
                        // A car that has taken its own chequered flag is on its cool-down lap: drawn, but not racing.
                        const finished = car.row.status === "finished";
                        const out = OUT_STATUSES.includes(car.row.status);
                        return (
                            <g
                                key={car.entryId}
                                data-testid="race-car"
                                data-entry-id={car.entryId}
                                data-motion-status={car.motion.status}
                                data-position-quality={car.quality}
                                data-relocation={car.motion.relocation ? "held" : reacquired ? "reacquired" : undefined}
                                data-finished={finished ? "true" : undefined}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    selectEntry(car.entryId);
                                }}
                                onMouseEnter={() => setHoveredId(car.entryId)}
                                onMouseLeave={() => setHoveredId(null)}
                                style={{ cursor: "pointer" }}
                                tabIndex={0}
                                role="button"
                                aria-label={`${car.row.acronym} P${car.row.position ?? "-"}`}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        selectEntry(car.entryId);
                                    }
                                }}
                            >
                                {active ? <circle cx={car.x} cy={car.y} r="14" fill={car.row.team_colour || "#E10600"} opacity="0.28" filter={`url(#glow-${idPrefix})`} /> : null}
                                <CarGlyph x={car.x} y={car.y} heading={car.heading} colour={car.row.team_colour || "#E10600"} active={active} dim={unreliable || finished || out} number={car.row.driver_number} />
                                {unreliable ? <circle cx={car.x} cy={car.y} r="11" fill="none" stroke="rgba(251,191,36,0.7)" strokeWidth="1.2" strokeDasharray="3 3" /> : null}
                                {estimate && !unreliable ? <circle cx={car.x} cy={car.y} r="11" fill="none" stroke="rgba(125,211,252,0.85)" strokeWidth="1.2" strokeDasharray="1 2.5" /> : null}
                                {reacquired ? <circle cx={car.x} cy={car.y} r="15" fill="none" stroke="rgba(251,113,133,0.85)" strokeWidth="1.3" strokeDasharray="4 2" /> : null}
                                {finished ? <circle cx={car.x} cy={car.y} r="13" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1" strokeDasharray="2 2" /> : null}
                            </g>
                        );
                    })}
                    {labelled.map((car) => {
                        const label = labels.get(car.entryId);
                        if (!label) return null;
                        const active = car.entryId === selectedEntryId || car.entryId === compareEntryId || car.entryId === hoveredId;
                        return (
                            <g key={`label-${car.entryId}`} data-testid="race-car-label" onClick={(event) => { event.stopPropagation(); selectEntry(car.entryId); }} style={{ cursor: "pointer" }}>
                                <line x1={car.x} y1={car.y} x2={label.x} y2={label.y + (label.y > car.y ? -label.h / 2 : label.h / 2)} stroke={active ? "rgba(255,255,255,0.42)" : "rgba(255,255,255,0.14)"} strokeWidth="1" />
                                <rect x={label.x - label.w / 2} y={label.y - label.h / 2} width={label.w} height={label.h} rx="11" fill={active ? "rgba(14,14,18,0.94)" : "rgba(8,8,10,0.88)"} stroke={active ? "rgba(225,6,0,0.45)" : "rgba(255,255,255,0.08)"} />
                                <circle cx={label.x - label.w / 2 + 10} cy={label.y} r="3" fill={car.row.team_colour || "#E10600"} />
                                <text x={label.x - label.w / 2 + 17} y={label.y + 3.4} fontFamily="JetBrains Mono, monospace" fontSize="9.5" fontWeight="700" fill="#FFFFFF">
                                    {`${car.row.position ?? "-"} ${car.row.acronym}${car.row.status === "finished" ? " ⚑" : OUT_STATUSES.includes(car.row.status) ? " out" : ""}`}
                                </text>
                            </g>
                        );
                    })}
                </g>
            </svg>

            <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full border border-white/8 bg-black/55 p-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 backdrop-blur-xl" onClick={(event) => event.stopPropagation()} role="group" aria-label="Label density">
                {LABEL_MODES.map((item) => (
                    <button key={item} type="button" aria-pressed={labelMode === item} onClick={() => setLabelMode(item)} className={`rounded-full px-3 py-1.5 transition ${labelMode === item ? "bg-white text-black" : "text-zinc-500 hover:bg-white/8 hover:text-zinc-200"}`}>
                        {item}
                    </button>
                ))}
            </div>

            <div className="pointer-events-none absolute bottom-4 left-5 flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                <span className="rounded-full bg-black/55 px-3 py-1.5 backdrop-blur-xl">{mode === "live" ? "Live positions" : "Replay"} · {layout.name}</span>
                <span className="rounded-full bg-black/55 px-3 py-1.5 backdrop-blur-xl">{layout.geometry_kind === "driven_reference_lap" ? "Reference lap geometry" : layout.geometry_kind}</span>
                {!coverage.pit_lane || !projector.hasPit ? <span className="rounded-full bg-black/55 px-3 py-1.5 text-amber-200/80 backdrop-blur-xl">Pit lane not measured</span> : null}
                {finishState === "leader_finished" ? <span className="rounded-full bg-black/55 px-3 py-1.5 text-zinc-300 backdrop-blur-xl" data-testid="race-finish-note" data-finish-state="leader_finished">⚑ leader finished · {stillRacing} {stillRacing === 1 ? "car" : "cars"} finishing</span> : null}
                {finishState === "all_finished" ? <span className="rounded-full bg-black/55 px-3 py-1.5 text-zinc-300 backdrop-blur-xl" data-testid="race-finish-note" data-finish-state="all_finished">⚑ all cars finished · cool-down, not racing</span> : null}
                {!coverage.sectors ? <span className="rounded-full bg-black/55 px-3 py-1.5 text-amber-200/80 backdrop-blur-xl">Sector lines not measured</span> : null}
            </div>

            {withoutPosition.length ? (
                <div className="pointer-events-none absolute bottom-4 right-5 max-w-[46%] rounded-2xl border border-white/8 bg-black/60 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500 backdrop-blur-xl" data-testid="race-no-position">
                    No position signal: {withoutPosition.map((row) => {
                        const motion = renderEntryMotion(motionStore, row.entry_id, t, layoutLengthM);
                        // Only motion chunks near the cursor are loaded, so the true time of the last sample may be unknown here.
                        return motion.sampleAge != null && motion.sampleAge > HIDE_STALE_AFTER_SECONDS ? `${row.acronym} (no sample for > ${HIDE_STALE_AFTER_SECONDS} s)` : row.acronym;
                    }).join(" · ")}
                </div>
            ) : null}
        </div>
    );
}
